import { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc, query, orderBy, limit, getDocs, where } from "firebase/firestore";
import toast from 'react-hot-toast';

// Condiciones corporativas reales de la agencia
const CONDICIONES_KOKIMIKO = [
    "Reserva asegurada con el abono del 50% del total",
    "El 50% restante se liquida el día del evento",
    "Uso exclusivo de la Party Zone durante las 3 horas",
    "Incluye animador especializado y limpieza posterior",
    "Los daños a las instalaciones serán facturados aparte"
];

const getInitialState = () => ({
    numeroPresupuesto: 'Calculando...',
    fechaPresupuesto: new Date().toISOString().split('T')[0], 
    datosCliente: { name: '', nif: '', direccion: '', email: '', telefono: '', codPostal: '', poblacion: '' },
    clienteSeleccionado: null,
    bloquesPresupuesto: [], // Inicia vacío para seleccionar paquetes predefinidos
    descuentoPorcentaje: 0,
    condicionesSeleccionadas: [CONDICIONES_KOKIMIKO[0], CONDICIONES_KOKIMIKO[1], CONDICIONES_KOKIMIKO[2]],
    estado: 'Borrador',
    pedidoVinculado: null
});

// Ahora cada línea soporta un tipo de IVA independiente (21% por defecto)
function createNewLine() {
    return { id: Math.random().toString(), descripcion: '', cantidad: 1, costo: 0, total: 0, tipoIva: 21 };
}

export function usePresupuestoForm(clientesDB, productosDB, optionsClientes) {
    const [state, setState] = useState(getInitialState());
    const [editingId, setEditingId] = useState(null);

    const generarSiguienteNumero = useCallback(async () => {
        const year = new Date().getFullYear();
        const prefix = `COT-${year}-`;
        const q = query(collection(db, 'presupuestos'), where('numero', '>=', prefix), where('numero', '<', `COT-${year + 1}-`), orderBy('numero', 'desc'), limit(1));
        try {
            const querySnapshot = await getDocs(q);
            let nextNum = 1;
            if (!querySnapshot.empty) {
                const lastNumStr = querySnapshot.docs[0].data().numero || `COT-${year}-000`;
                const lastNum = parseInt(lastNumStr.split('-')[2], 10);
                nextNum = lastNum + 1;
            }
            const newNumero = `${prefix}${String(nextNum).padStart(3, '0')}`;
            setState(s => ({ ...s, numeroPresupuesto: newNumero }));
        } catch (error) {
            console.error("Error generando número:", error);
            setState(s => ({ ...s, numeroPresupuesto: 'Error' }));
        }
    }, []);

    useEffect(() => { 
        if (!editingId) {
            generarSiguienteNumero();
        } 
    }, [editingId, generarSiguienteNumero]);

    // 🔥 CÁLCULOS AVANZADOS: División automática de IVA 10% y 21% 🔥
    const calculations = useMemo(() => {
        const descuentoPct = parseFloat(state.descuentoPorcentaje) || 0;
        const descFactor = 1 - (descuentoPct / 100);
        
        let base21 = 0;
        let base10 = 0;

        const todasLasLineas = (state.bloquesPresupuesto || []).reduce((acc, b) => [...acc, ...(b.lineas || [])], []);

        todasLasLineas.forEach(line => {
            const totalLinea = parseFloat(line.total) || 0;
            const ivaAplicable = line.tipoIva === 10 ? 10 : 21;
            
            if (ivaAplicable === 10) {
                base10 += totalLinea;
            } else {
                base21 += totalLinea;
            }
        });

        const subtotal = base21 + base10;
        
        const base21Descuento = base21 * descFactor;
        const base10Descuento = base10 * descFactor;

        const iva21 = base21Descuento * 0.21;
        const iva10 = base10Descuento * 0.10;
        const ivaTotal = iva21 + iva10;

        const totalGeneral = base21Descuento + base10Descuento + ivaTotal;
        const descuentoImporte = subtotal - (base21Descuento + base10Descuento);

        return {
            subtotal, 
            totales: { 
                subtotalParaPDF: subtotal, 
                iva: ivaTotal,
                base21: base21Descuento,
                base10: base10Descuento,
                iva21,
                iva10
            },
            descuentoImporte, 
            totalGeneral 
        };
    }, [state]);

    const resetForm = useCallback(() => {
        setState(getInitialState()); 
        setEditingId(null);
        generarSiguienteNumero();
    }, [generarSiguienteNumero]);

    const handleSelectCliente = (selectedOption) => {
        if (selectedOption) {
            const cliente = clientesDB.find(c => c.id === selectedOption.value);
            if (cliente) {
                setState(s => ({ 
                    ...s, 
                    clienteSeleccionado: selectedOption, 
                    datosCliente: { 
                        name: cliente.nombreRepresentante || cliente.name || '', 
                        nif: cliente.dni || cliente.nif || '', 
                        direccion: cliente.direccion || '', 
                        email: cliente.email || '', 
                        telefono: cliente.telefono || '', 
                        codPostal: cliente.codPostal || '', 
                        poblacion: cliente.poblacion || '' 
                    } 
                }));
            }
        } else { 
            const freshInitial = getInitialState();
            setState(s => ({ ...s, clienteSeleccionado: null, datosCliente: freshInitial.datosCliente })); 
        }
    };

    const handleClienteChange = (e) => { 
        const { name, value } = e.target; 
        setState(s => ({ ...s, datosCliente: { ...s.datosCliente, [name]: value } })); 
    };

    const handleLineChange = (bloqueId, lineId, field, value) => {
        setState(s => {
            const nuevosBloques = s.bloquesPresupuesto.map(b => {
                if (b.id === bloqueId) {
                    const newLineas = b.lineas.map(line => {
                        if (line.id === lineId) {
                            const updatedLine = { ...line, [field]: value };
                            if (field === 'cantidad' || field === 'costo') {
                                const cant = parseFloat(updatedLine.cantidad) || 0;
                                const cost = parseFloat(updatedLine.costo) || 0;
                                updatedLine.total = cant * cost;
                            }
                            return updatedLine;
                        }
                        return line;
                    });
                    return { ...b, lineas: newLineas };
                }
                return b;
            });
            return { ...s, bloquesPresupuesto: nuevosBloques };
        });
    };

    // 🔥 CARGA RÁPIDA DE PAQUETES KOKIMIKO 🔥
    const addPaqueteBaby = () => {
        setState(s => ({
            ...s,
            bloquesPresupuesto: [...(s.bloquesPresupuesto || []), {
                id: Math.random().toString(),
                nombre: 'Opción A: Mis Primeros Pasos',
                descripcion: 'Recepción en Zona Baby, 30 min estimulación bilingüe, burbujas y juego libre (0 a 3 años).',
                abierta: true,
                lineas: [
                    { id: Math.random().toString(), descripcion: 'Paquete Evento (Exclusividad fin de semana)', cantidad: 1, costo: 450, total: 450, tipoIva: 21 },
                    { id: Math.random().toString(), descripcion: 'Cotillón Eco-Friendly Baby', cantidad: 6, costo: 4.50, total: 27, tipoIva: 21 }
                ]
            }]
        }));
        toast.success("Paquete Baby añadido");
    };

    const addPaqueteArtPlay = () => {
        setState(s => ({
            ...s,
            bloquesPresupuesto: [...(s.bloquesPresupuesto || []), {
                id: Math.random().toString(),
                nombre: 'Opción B: Art & Play',
                descripcion: 'Taller Temático (Glow, Slime o Lego), merienda y juego en parque ecológico (4 a 12 años).',
                abierta: true,
                lineas: [
                    { id: Math.random().toString(), descripcion: 'Niños asistentes (Taller Standard)', cantidad: 12, costo: 22, total: 264, tipoIva: 21 },
                    { id: Math.random().toString(), descripcion: 'Cotillón Maker (Plantas y Lápices)', cantidad: 12, costo: 4.60, total: 55.20, tipoIva: 21 }
                ]
            }]
        }));
        toast.success("Paquete Art & Play añadido");
    };

    const addCateringPadres = () => {
        setState(s => ({
            ...s,
            bloquesPresupuesto: [...(s.bloquesPresupuesto || []), {
                id: Math.random().toString(),
                nombre: 'Catering para Padres',
                descripcion: 'Selección premium de alimentos y bebidas para adultos.',
                abierta: true,
                lineas: [
                    { id: Math.random().toString(), descripcion: 'Tabla Ibérica Completa', cantidad: 1, costo: 55, total: 55, tipoIva: 10 },
                    { id: Math.random().toString(), descripcion: 'Termo Café de Especialidad + Galletas', cantidad: 1, costo: 30, total: 30, tipoIva: 10 }
                ]
            }]
        }));
        toast.success("Catering añadido (IVA 10%)");
    };

    const addManualLine = (bloqueId) => setState(s => ({
        ...s,
        bloquesPresupuesto: s.bloquesPresupuesto.map(b => 
            b.id === bloqueId ? { ...b, lineas: [...b.lineas, createNewLine()] } : b
        )
    }));

    const removeLine = (bloqueId, lineId) => setState(s => ({
        ...s,
        bloquesPresupuesto: s.bloquesPresupuesto.map(b => 
            b.id === bloqueId ? { ...b, lineas: b.lineas.filter(l => l.id !== lineId) } : b
        )
    }));

    const handleCondicionesChange = (condicion) => { 
        setState(prevState => { 
            const ya = prevState.condicionesSeleccionadas.includes(condicion); 
            return { ...prevState, condicionesSeleccionadas: ya ? prevState.condicionesSeleccionadas.filter(c => c !== condicion) : [...prevState.condicionesSeleccionadas, condicion] }; 
        }); 
    }; 
    
    const handleSaveBudget = async () => {
        if (!state.datosCliente.name) return toast.error("Falta nombre de la familia/cliente.");
        
        const presupuestoData = {
            numero: state.numeroPresupuesto,
            fecha: state.fechaPresupuesto,
            cliente: state.datosCliente,
            bloquesPresupuesto: state.bloquesPresupuesto, 
            condicionesSeleccionadas: state.condicionesSeleccionadas,
            calculos: { 
                ...calculations, 
                descuentoPorcentaje: state.descuentoPorcentaje 
            },
            totales: calculations.totales,
            estado: state.estado,
            clienteId: state.clienteSeleccionado?.value || null,
            updatedAt: serverTimestamp(),
        };

        const toastId = toast.loading('Guardando cotización...');

        try {
            if (editingId) {
                await updateDoc(doc(db, "presupuestos", editingId), presupuestoData);
                toast.success("¡Cotización actualizada!", { id: toastId });
            } else {
                presupuestoData.createdAt = serverTimestamp();
                await addDoc(collection(db, "presupuestos"), presupuestoData);
                toast.success("¡Cotización creada!", { id: toastId });
            }
            resetForm();
        } catch (e) {
            console.error("Error al guardar:", e);
            toast.error("Error al guardar.", { id: toastId });
        }
    };
    
    const handleEditarPresupuesto = (presupuesto, isCopy = false) => {
        const freshInitial = getInitialState();
        
        let bloquesMigrados = [];
        if (presupuesto.bloquesPresupuesto && presupuesto.bloquesPresupuesto.length > 0) {
            bloquesMigrados = presupuesto.bloquesPresupuesto.map(b => ({
                ...b,
                lineas: (b.lineas || []).map(l => ({
                    ...l,
                    tipoIva: l.tipoIva || 21 // Mantenemos compatibilidad con presupuestos viejos
                }))
            }));
        } else if (presupuesto.lineas && presupuesto.lineas.length > 0) {
            bloquesMigrados = [{
                id: Math.random().toString(),
                nombre: 'Paquete Principal',
                descripcion: presupuesto.descripcion || '',
                abierta: true,
                lineas: presupuesto.lineas.map(l => ({
                    id: Math.random().toString(),
                    descripcion: l.descripcion || l.producto || '',
                    cantidad: l.cantidad || 1,
                    costo: l.costo || l.pvp || 0,
                    total: l.total || 0,
                    tipoIva: l.tipoIva || 21
                }))
            }];
        }

        const datosCargados = {
            ...freshInitial,
            ...presupuesto,
            numeroPresupuesto: isCopy ? 'Calculando...' : (presupuesto.numeroPresupuesto || presupuesto.numero),
            fechaPresupuesto: isCopy ? freshInitial.fechaPresupuesto : (presupuesto.fechaPresupuesto || presupuesto.fecha),
            datosCliente: presupuesto.cliente || presupuesto.datosCliente || freshInitial.datosCliente,
            descuentoPorcentaje: presupuesto.calculos?.descuentoPorcentaje || presupuesto.descuentoPorcentaje || 0,
            condicionesSeleccionadas: presupuesto.condicionesSeleccionadas || freshInitial.condicionesSeleccionadas,
            bloquesPresupuesto: bloquesMigrados,
        };

        setState(datosCargados);
        
        if (isCopy) {
            setEditingId(null);
            generarSiguienteNumero();
        } else {
            setEditingId(presupuesto.id);
        }
    };

    const handleBloqueChange = (bloqueId, field, value) => {
        setState(s => ({ ...s, bloquesPresupuesto: s.bloquesPresupuesto.map(b => b.id === bloqueId ? { ...b, [field]: value } : b) }));
    };

    const toggleBloque = (bloqueId) => {
        setState(s => ({ ...s, bloquesPresupuesto: s.bloquesPresupuesto.map(b => b.id === bloqueId ? { ...b, abierta: !b.abierta } : b) }));
    };

    const addBloque = () => {
        setState(s => ({ 
            ...s, 
            bloquesPresupuesto: [...(s.bloquesPresupuesto || []), { id: Math.random().toString(), nombre: `Sección ${(s.bloquesPresupuesto?.length || 0) + 1}`, descripcion: '', abierta: true, lineas: [] }] 
        }));
    };

    const removeBloque = (bloqueId) => {
        if(window.confirm("¿Eliminar esta sección completa?")) {
            setState(s => ({ ...s, bloquesPresupuesto: s.bloquesPresupuesto.filter(b => b.id !== bloqueId) }));
        }
    };

    return { 
        state, setState, editingId, setEditingId, calculations, resetForm, 
        handleSelectCliente, handleClienteChange, handleLineChange, 
        addManualLine, removeLine, 
        handleSaveBudget, handleEditarPresupuesto, handleCondicionesChange, generarSiguienteNumero,
        handleBloqueChange, toggleBloque, addBloque, removeBloque,
        addPaqueteBaby, addPaqueteArtPlay, addCateringPadres // Exportamos las nuevas funciones
    };
}